import React from 'react';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import {
  Avatar,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  ChipProps,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import moment from 'moment';

import {
  ICourseTutor,
  deleteCourseTutor,
  updateCourseTutor,
} from '../../../../../../../../store/V2/CourseTutorsSlice';
import { getCoursesState } from '../../../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../../../hooks';
import { getAuthState } from '../../../../../../../../store/AuthSlice';
import { checkUserPermission } from '../../../../../../../../helper';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../../store/V2/RolesSlice';

interface CourseTutorCardProps {
  courseTutor: ICourseTutor;
  onEdit: () => void;
}

const containerStyles: SxProps = {
  backgroundColor: '#F8F9FD',
  borderRadius: '12px',
};

const getFormatedStatus = (status: ICourseTutor['status']) => {
  let newStatus = '';

  switch (status) {
    case 'ENABLE':
      newStatus = 'Enabled';
      break;
    case 'DISABLE':
      newStatus = 'Disabled';
      break;

    default:
      newStatus = '';
      break;
  }

  return newStatus;
};

const CourseTutorCard: React.FC<CourseTutorCardProps> = ({
  courseTutor,
  onEdit,
}) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Store
  const coursesState = useAppSelector(getCoursesState);
  const authState = useAppSelector(getAuthState);

  const courseData = coursesState.adminCourse;
  const { tutor } = courseTutor;

  let tutorName = '';

  const statusChipProps: ChipProps = {
    size: 'small',
    label: getFormatedStatus(courseTutor.status),
    color: 'success',
  };

  if (courseTutor.status !== 'ENABLE') {
    statusChipProps.color = 'error';
  }

  if (tutor) {
    tutorName = tutor.firstname || '';

    if (tutor.lastname) {
      tutorName += ` ${tutor.lastname}`;
    }
  }

  const handleDelete = () => {
    confirm({
      title: 'Are you sure?',
      description: `Do you want to delete ${tutorName}?`,
    }).then(() => {
      if (courseData) {
        dispatch(
          deleteCourseTutor({
            id: courseTutor._id,
            course: courseData._id,
          })
        );
      }
    });
  };

  const changeStatus = (status: ICourseTutor['status']) => {
    confirm({
      title: 'Are you sure?',
      description: `Do you want to update ${tutorName}'s status?`,
    }).then(() => {
      if (courseData) {
        dispatch(
          updateCourseTutor({
            course: courseData._id,
            id: courseTutor._id,
            status: status === 'ENABLE' ? 'DISABLE' : 'ENABLE',
          })
        );
      }
    });
  };

  return (
    <Card elevation={0} sx={containerStyles}>
      <CardContent sx={{ p: '16px' }}>
        {tutor ? (
          <Stack direction="row" alignItems="center" gap={2} mb="15px">
            <Avatar src={tutor.avatar} />
            <Stack flexGrow={1}>
              <Typography variant="subtitle2" fontWeight={600}>
                {tutor.firstname}
                {tutor.lastname ? ` ${tutor.lastname}` : ''}
              </Typography>
              <Typography variant="caption">{tutor.email}</Typography>
            </Stack>
            <Chip {...statusChipProps} />
            {courseTutor.type ? (
              <Chip
                {...statusChipProps}
                color="default"
                label={courseTutor.type.toLowerCase()}
                sx={{ textTransform: 'capitalize' }}
              />
            ) : null}
          </Stack>
        ) : null}
        {courseTutor.startDate ? (
          <Stack mb="15px">
            <Typography variant="subtitle2" fontWeight={600}>
              Start Date
            </Typography>
            <Typography>
              {moment(courseTutor.startDate).format('ddd MMM DD YYYY')}
            </Typography>
          </Stack>
        ) : null}
        {courseTutor.endDate ? (
          <Stack mb="15px">
            <Typography variant="subtitle2" fontWeight={600}>
              End Date
            </Typography>
            <Typography>
              {moment(courseTutor.endDate).format('ddd MMM DD YYYY')}
            </Typography>
          </Stack>
        ) : null}
        {courseTutor.title ? (
          <Stack mb="15px">
            <Typography variant="subtitle2" fontWeight={600}>
              Title
            </Typography>
            <Typography>{courseTutor.title}</Typography>
          </Stack>
        ) : null}
        {courseTutor.description ? (
          <Stack mb="15px">
            <Typography variant="subtitle2" fontWeight={600}>
              Description
            </Typography>
            <div
              className="editor-container"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: courseTutor.description || '',
              }}
            />
          </Stack>
        ) : null}
        {checkUserPermission(authState.user, {
          adminCourse: [USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_EDIT],
        }) ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={2}
          >
            <ButtonBase
              onClick={handleDelete}
              sx={{
                backgroundColor: '#F1F5F9',
                padding: '5px',
                borderRadius: '5px',
              }}
            >
              <DeleteIcon color="error" />
            </ButtonBase>
            <ButtonBase
              onClick={() => onEdit()}
              sx={{
                backgroundColor: '#F1F5F9',
                padding: '5px',
                borderRadius: '5px',
              }}
            >
              <EditIcon />
            </ButtonBase>
            <Button
              size="small"
              variant="outlined"
              color={courseTutor.status === 'ENABLE' ? 'error' : 'info'}
              onClick={() => changeStatus(courseTutor.status)}
            >
              {courseTutor.status === 'ENABLE' ? 'Disable' : 'Enable'}
            </Button>
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default CourseTutorCard;
